import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Camera, ListChecks, Timer, Contact, MessageSquare, LayoutDashboard, Building2, FileText, LogOut, Menu, X, Image as ImageIcon, CheckSquare, Calendar as CalendarIcon, Activity, LayoutGrid, Bell, Sun, Moon, Send, Shield, CloudUpload, Download, Clapperboard, Plug, Inbox, Gauge, CalendarClock, MessagesSquare, Palette, BarChart3, Landmark } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import ViewAsBanner from '@/components/shell/ViewAsBanner';

import { useTheme } from '@/hooks/use-theme';
import { fetchUnreadCount, fetchWaitingOnMe } from '@/lib/queries';
import { useIsAdmin, useTasksOnly } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';
import { akeys, fetchAnalyticsMe } from '@/lib/analytics';
import { useUploadCenter } from '@/lib/upload-center';
import { useDownloadCenter } from '@/lib/download-center';

const NAV = [
  { to: '/',             label: 'Dashboard',  icon: LayoutDashboard, managerOnly: false },
  { to: '/tasks',         label: 'My Tasks',     icon: CheckSquare,      managerOnly: false },
  { to: '/notifications', label: 'Notifications', icon: Bell,            managerOnly: false },
  { to: '/chats',         label: 'Chats',         icon: MessageSquare,   managerOnly: false },
  { to: '/client-chats',  label: 'Client chats',  icon: MessagesSquare,  managerOnly: false },
  { to: '/shoots',        label: 'Shoots',        icon: Camera,          managerOnly: false },
  { to: '/tracker',       label: 'Tracker',       icon: ListChecks,      managerOnly: false },
  // wideOnly: planning and client surfaces that a tasks-level executor cannot
  // reach (the API refuses them), so the nav does not offer dead doors.
  { to: '/meetings',     label: 'Meetings',   icon: CalendarClock,    managerOnly: false },
  { to: '/calendar',     label: 'Calendar',   icon: CalendarIcon,     managerOnly: false, wideOnly: true },
  { to: '/clients',      label: 'Clients',    icon: Building2,        managerOnly: false, wideOnly: true },
  { to: '/strategies',   label: 'Strategies', icon: FileText,         managerOnly: false, wideOnly: true },
  { to: '/creatives',    label: 'Creatives',  icon: ImageIcon,         managerOnly: false },
  // Everyone on a clinic uses its brand kit, executors included, so this is not wideOnly.
  { to: '/brand-kits',   label: 'Brand kits', icon: Palette,           managerOnly: false },
  // Everyone uploads, executors included, so this is not wideOnly.
  { to: '/uploads',      label: 'Upload Center', icon: CloudUpload,    managerOnly: false },
  { to: '/downloads',    label: 'Download Center', icon: Download,     managerOnly: false },
  { to: '/material',     label: 'Files',        icon: Clapperboard,     managerOnly: false, wideOnly: true },
  { to: '/library',      label: 'Library',    icon: LayoutGrid,        managerOnly: false, wideOnly: true },
  { to: '/requests',     label: 'Client requests', icon: Send,          managerOnly: false, wideOnly: true },
  // analyticsOnly: shown when GET /analytics/me says so (owner, manager, or a door the owner opened)
  { to: '/analytics',    label: 'Analytics',       icon: BarChart3,     managerOnly: false, analyticsOnly: true },
  { to: '/capacity',     label: 'Team capacity',   icon: Activity,    managerOnly: true  },
  { to: '/sla',          label: 'Services SLA',    icon: Timer,       managerOnly: true  },
  { to: '/client-users', label: 'Clients users',   icon: Contact,     managerOnly: true  },
  { to: '/connections',  label: 'Connections',     icon: Plug,        managerOnly: true  },
  { to: '/messages',     label: 'Message log',     icon: Inbox,       managerOnly: true  },
  { to: '/transfers',    label: 'Transfer speeds', icon: Gauge,       managerOnly: true  },
  // People and invites live inside Admin now; one door, not three.
  { to: '/admin',        label: 'Admin',           icon: Shield,    managerOnly: true  },
];

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const isManager = useIsAdmin();
  const [open, setOpen] = useState(false);
  const uploads = useUploadCenter();
  const downloads = useDownloadCenter();

  // what waits on my decision, counted on My Tasks so it is seen from anywhere
  const waiting = useQuery({ queryKey: ['waiting-on-me'], queryFn: fetchWaitingOnMe, refetchInterval: 60_000, enabled: !!user && user.role !== 'client' });
  const unread = useQuery({
    queryKey: ['unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 30_000,
    enabled: !!user,
  });
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const onPartners = useLocation().pathname.startsWith('/partners');

  const display = user?.display_name || user?.email || 'User';
  // Until the scope answer arrives, hide the clinic-wide entries: a task-only
  // person must never see Strategies / Clients flash in before the nav settles.
  const hideWide = useTasksOnly() !== false;
  const analytics = useQuery({ queryKey: akeys.me, queryFn: fetchAnalyticsMe, staleTime: 5 * 60_000, enabled: !!user });
  const navItems = NAV.filter((n) => (!n.managerOnly || isManager) && !((n as { wideOnly?: boolean }).wideOnly && hideWide)
    && (!(n as { analyticsOnly?: boolean }).analyticsOnly || !!analytics.data?.can_view));

  return (
    <div className="min-h-screen flex flex-col">
    <ViewAsBanner />
    <div className="flex-1 flex flex-col md:flex-row">
      {/* mobile top bar */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-bg-card">
        <Link to="/" className="flex items-center gap-2">
          <img src="/kaf-logo.png" alt="KAF" className="h-7 w-7 rounded-full" />
          <span className="text-accent-400 font-semibold text-sm">KAF Warehouse</span>
        </Link>
        <button onClick={() => setOpen(!open)} aria-label={open ? 'Close menu' : 'Open menu'} className="text-muted-strong p-2 -mr-2 rounded-lg active:bg-bg-card">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      <aside
        className={cn(
          'bg-bg-card md:bg-bg-soft border-r border-bg-card md:w-60 md:min-h-screen p-4 md:p-5 flex-col gap-1',
          open ? 'flex' : 'hidden md:flex'
        )}
      >
        <Link to="/" className="hidden md:flex items-center gap-3 mb-6">
          <img src="/kaf-logo.png" alt="KAF" className="h-9 w-9 rounded-full" />
          <div className="leading-tight">
            <div className="text-accent-400 text-base font-semibold">KAF Warehouse</div>
            <div className="text-[9px] text-muted uppercase tracking-widest">KAF PRO</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, label, icon: Icon }) => {
            const badge = to === '/notifications' ? (unread.data ?? 0)
              : to === '/tasks' ? (waiting.data?.length ?? 0)
              : to === '/uploads' ? uploads.active + uploads.queued + uploads.failed
              : to === '/downloads' ? downloads.active + downloads.queued + downloads.failed
              : 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition',
                    isActive
                      ? 'bg-accent-600/20 text-accent-400'
                      : 'text-muted hover:bg-bg-card hover:text-muted-strong'
                  )
                }
              >
                <Icon size={18} />
                <span className="flex-1">{label}</span>
                {badge > 0 && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-accent-500 text-white min-w-[18px] text-center">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-auto pt-6 border-t border-line text-xs text-muted">
          {/* The partners' toggle: drawn only for the three partners (Aziz 2026-10-30). */}
          {user?.partner && (
            <button role="switch" aria-checked={onPartners} data-testid="partners-toggle"
              onClick={() => { setOpen(false); navigate(onPartners ? '/' : '/partners'); }}
              className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg w-full text-left hover:bg-bg-card text-muted-strong">
              <Landmark size={14} className="text-accent-400" />
              <span className="flex-1">Partners</span>
              <span className={cn('relative h-4 w-7 rounded-full transition', onPartners ? 'bg-accent-500' : 'bg-slate-700')}>
                <span className={cn('absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', onPartners ? 'left-3.5' : 'left-0.5')} />
              </span>
            </button>
          )}
          <div className="px-3 mb-2 truncate" title={display}>{display}</div>
          {user?.role && (
            <div className="px-3 mb-3 text-accent-400/80 uppercase tracking-wide text-[10px]">
              {user.role}
            </div>
          )}
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left hover:bg-bg-card text-muted"
          >
            <Send size={14} /> Profile + Telegram
          </Link>
          <button
            onClick={toggleTheme}
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            className="flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left hover:bg-bg-card"
          >
            {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
            {theme === 'light' ? 'Dark mode' : 'Light mode'}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-2 rounded-lg w-full text-left hover:bg-bg-card"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 md:p-8 max-w-6xl mx-auto w-full">
        {children}
      </main>
    </div>
    </div>
  );
}
